import { Link } from '@tanstack/react-router';
import { ChevronRight, RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { diffInMs, formatDurationMs } from '~/utils/date';

import { useBatchRetry } from '../hooks/use-batch-retry';
import { useRecoveryFailurePoints } from '../hooks/use-recovery-failure-points';
import { useRunFailureText } from '../hooks/use-run-failure-text';
import { useFunnelSummary } from '../hooks/use-run-funnel';
import { useRunLabels } from '../hooks/use-run-labels';
import type { RunProgress, RunRead } from '../types/run.api.types';
import { batchCounts } from '../utils/batch-counts';
import { isRecentRun } from '../utils/batches-table';
import { controlErrorMessage } from '../utils/run-control-errors';
import { isRunStopped } from '../utils/run-status';

interface LineProps {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress;
  /** The state's own sentence from `useRunNextStep` (e.g. "Waiting for your review at Sample"). */
  label: string | null;
  /** Its hint (e.g. "Waiting since …"). */
  hint: string | null;
  /** Whether a stop is recent (the 7-day rule) when there is no run record to tell (experiments). */
  recent?: boolean | undefined;
}

/**
 * The header's one state sentence (guide part 2 §2.1): the only place the frame explains where
 * the batch stands. Its colour follows the badge — amber while it waits on a person, red for a
 * stop in the last 7 days, muted otherwise (never a tinted box, never green).
 */
export function RunStateLine({ runId, run, progress, label, hint, recent }: LineProps) {
  if (isRunStopped(progress.runStatus)) {
    return <StoppedLine runId={runId} run={run} progress={progress} recent={recent} />;
  }
  return <MovingLine runId={runId} progress={progress} label={label} hint={hint} />;
}

/** Running, waiting, paused or finished: one sentence, amber only while a person is needed. */
function MovingLine({
  runId,
  progress,
  label,
  hint,
}: {
  runId: string;
  progress: RunProgress;
  label: string | null;
  hint: string | null;
}) {
  const funnel = useFunnelSummary(runId).data;
  const summary = funnel?.kind === 'ok' ? funnel.summary : null;
  // The batch's product count in the module's one vocabulary (summary line, else progress totals).
  const products = batchCounts(summary, progress).products;
  const { t } = useTranslation('catalog-enrichment-run');
  const { stageLabel } = useRunLabels();
  const stage = progress.currentStage ? stageLabel(progress.currentStage) : null;
  const waiting = !!progress.pendingGate || !!progress.costGate || progress.paused;
  const done = progress.runStatus === 'COMPLETED';
  const totalMs =
    progress.startedAt && progress.completedAt
      ? diffInMs(progress.startedAt, progress.completedAt)
      : null;
  // A batch that finished with no products had nothing to process — never "went through all its steps".
  const empty = done && products === 0;
  const sentence = empty
    ? t('whatHappened.completedEmpty')
    : done
      ? totalMs !== null
        ? t('whatHappened.completed', { time: formatDurationMs(totalMs) })
        : t('whatHappened.completedNoTime')
      : progress.paused
        ? t('whatHappened.paused', { stage: stage ?? '' })
        : (label ?? (stage ? t('whatHappened.atStage', { stage }) : t('whatHappened.starting')));
  return (
    <StateText tone={waiting ? 'text-warning' : 'text-secondary-text'}>
      {sentence}
      {hint ? <span className="text-secondary-text"> · {hint}</span> : null}
    </StateText>
  );
}

/** A stopped batch: where and why it stopped (the reviewer's words or the plain error). */
function StoppedLine({
  runId,
  run,
  progress,
  recent: recentHint,
}: {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress;
  recent: boolean | undefined;
}) {
  const { title, why, next, raw, ready } = useRunFailureText(runId, progress);
  // The 7-day rule: an old stop is history, not an alarm.
  const recent = run ? isRecentRun(run) : (recentHint ?? true);
  // Wait for the attempts: "Stopped at X" swapping to "Rejected at X" a moment later reads as a change.
  if (!ready) return <Skeleton className="h-5 w-80 max-w-full" />;
  return (
    <StateText tone={recent ? 'text-error' : 'text-secondary-text'} title={raw || undefined}>
      <span className="font-medium">{title}.</span>{' '}
      <span className="text-foreground">{/[.!?]$/.test(why.trim()) ? why : `${why}.`}</span>
      {next ? <span className="text-secondary-text"> {next}</span> : null}
    </StateText>
  );
}

/** The sentence's frame: one line of body text in the state's tone. */
function StateText({
  tone,
  title,
  children,
}: {
  tone: string;
  title?: string | undefined;
  children: ReactNode;
}) {
  return (
    <p
      data-testid="catalog-enrichment-run-state-line"
      title={title}
      className={cn('text-sm leading-relaxed break-words', tone)}
    >
      {children}
    </p>
  );
}

/**
 * A stopped batch's one primary action, offering only what the backend accepts: "Open the newer
 * batch" when one replaced it, "Retry from <step>" while that step's retry option is open, else
 * "Retry options" (the Fix & retry page). Null for a batch that hasn't stopped.
 */
export function RunStoppedPrimary({ runId, progress }: { runId: string; progress: RunProgress }) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tRuns } = useTranslation('catalog-enrichment-runs');
  const { stageLabel } = useRunLabels();
  const stopped = isRunStopped(progress.runStatus);
  const retry = useBatchRetry();
  const failurePoints = useRecoveryFailurePoints(runId, false, stopped);
  const supersededBy = failurePoints.data?.supersededBy ?? null;
  // Wait for the answer: offering "Retry from <step>" first and swapping to "Open the newer
  // batch" a moment later would invite a click the backend then refuses.
  if (!stopped || failurePoints.isPending) return null;

  const stage = progress.currentStage ?? null;
  const option = stage ? progress.retryOptions?.find((o) => o.stage === stage) : undefined;

  if (supersededBy) {
    return (
      <Button asChild size="sm" className="cursor-pointer">
        <Link
          to="/catalog-enrichment/runs/$runId"
          params={{ runId: supersededBy }}
          data-testid="catalog-enrichment-run-open-newer-button"
        >
          {t('whatHappened.openNewer')}
          <ChevronRight aria-hidden="true" />
        </Link>
      </Button>
    );
  }
  if (stage && option?.allowed !== false) {
    return (
      <Button
        size="sm"
        className="cursor-pointer"
        isLoading={retry.isPending}
        data-testid="catalog-enrichment-run-retry-button"
        onClick={() => {
          retry.mutate(
            { runIds: [runId], mode: 'stage' },
            {
              onSuccess: ({ succeeded, failed }) => {
                if (succeeded.length > 0) toast.success(tRuns('retry.started.stage', { count: 1 }));
                const first = failed[0];
                if (first) {
                  toast.error(tRuns('retry.failed', { count: 1 }), {
                    description: controlErrorMessage(first.error, tRuns('archive.genericError')),
                  });
                }
              },
            },
          );
        }}
      >
        <RotateCcw aria-hidden="true" />
        {t('whatHappened.retryFrom', { stage: stageLabel(stage) })}
      </Button>
    );
  }
  return (
    <Button asChild size="sm" variant="outline" className="cursor-pointer">
      <Link
        to="/catalog-enrichment/runs/$runId/recovery"
        params={{ runId }}
        data-testid="catalog-enrichment-run-retry-options-button"
      >
        {t('whatHappened.moreOptions')}
      </Link>
    </Button>
  );
}
